import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import { getCachedShippingCost, setCachedShippingCost } from '../../utils/shippingCostCache';
import { fetchShopDeliveryInfo } from '../../utils/shopFreeShipping';
import { quoteShippingFromCart, FREE_SHIP_FALLBACK } from '../../utils/quoteShipping';
import { getSubtotalForFreeShippingEligible } from '../../utils/cartTotals';
import { loadGooglePlaces } from '../../utils/loadGooglePlaces';
import { cartLineNeedsShippingQuote } from '../../utils/cartShippingEligibility';
import {
  CHECKOUT_COUNTRIES,
  CHECKOUT_DIAL_CODES,
  countryToCodeFromGoogleOrLabel,
  isAustraliaCountryCode,
} from '../../constants/checkoutCountries';
import {
  parseCustomerShippingAddress,
  isCompleteShippingAddress,
  formatShippingAddressSummary,
  persistCustomerShippingAfterOrder,
} from '../../utils/customerShippingAddress';
import './CustomCheckout.css';

// Stripe publishable key is fetched at runtime from the business integrations API.
// Falls back to REACT_APP_STRIPE_PUBLISHABLE_KEY env var if the API is unavailable.
const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

async function fetchStripePublishableKey() {
  try {
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const url = `${backendUrl}/api/v1/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key from integrations API, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

export const AFTERPAY_SESSION_STORAGE_KEY = 'afterpayCheckoutSession';

/** SessionStorage key prefix for itemized receipt on success page (`${prefix}${orderNumber}`). */
export const ORDER_RECEIPT_STORAGE_PREFIX = 'rs_order_receipt_v1:';

/** donation | event | product | mixed — used for post-checkout thank-you line */
function computeOrderKind(cartItems, isDonationOnly) {
  if (isDonationOnly) return 'donation';
  if (!Array.isArray(cartItems) || !cartItems.length) return 'product';
  const isEvent = (i) => i.type === 'event' || i.eventId;
  const isDon = (i) => i.type === 'donation' || i.productType === 'donation';
  const hasE = cartItems.some(isEvent);
  const hasD = cartItems.some(isDon);
  const hasP = cartItems.some((i) => !isEvent(i) && !isDon(i));
  if ((hasE && hasP) || (hasE && hasD) || (hasD && hasP)) return 'mixed';
  if (hasE) return 'event';
  if (hasD) return 'donation';
  return 'product';
}

function lineSummaryFromCart(cartItems) {
  if (!Array.isArray(cartItems)) return '';
  return cartItems
    .map((i) => i.name || i.title || i.productName)
    .filter(Boolean)
    .join(' · ');
}

const CheckoutForm = ({ 
  clientSecret, 
  processing, 
  setProcessing, 
  error, 
  setError, 
  orderCompleted, 
  setOrderCompleted,
  /** Fires the exact subtotal−discount+shipping total (cents) used for the Place Order button — parent PaymentIntent must match to avoid overcharging. */
  onPayableAmountCentsChange
}) => {
  // Dev/staging only: set REACT_APP_CHECKOUT_BYPASS=true to skip Stripe (never enable in production builds).
  const BYPASS_PAYMENT = process.env.REACT_APP_CHECKOUT_BYPASS === 'true';

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const { customer, isAuthenticated: isShopCustomer, getAuthHeaders, refreshCustomer } = useCustomerAuth();
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    apartment: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'AU',
  });

  /** DOM ref for the Street Address input — used to attach Google Places Autocomplete. */
  const streetInputRef = useRef(null);
  const [autocompleteReady, setAutocompleteReady] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('card');

  // Donation-only cart: hide irrelevant fields
  const isDonationOnly = Array.isArray(cartItems) && cartItems.length > 0 &&
    cartItems.every(item => item.type === 'donation' || item.productType === 'donation');

  // When the customer already has a complete saved address, hide the form until they choose to edit.
  const [editShippingAddress, setEditShippingAddress] = useState(false);

  const savedProfileShipping = parseCustomerShippingAddress(customer?.shippingAddress);
  const hasSavedProfileShipping = isCompleteShippingAddress(savedProfileShipping);
  const usingSavedProfileShipping =
    isShopCustomer && hasSavedProfileShipping && !editShippingAddress;

  // What the courier should do if no one is home (sent to staff/carrier via order notes).
  const [deliveryPreference, setDeliveryPreference] = useState('');

  // Phone dialing code prefix (e.g. +61). Combined with the phone number when the order is sent.
  const [phoneDialCode, setPhoneDialCode] = useState('+61');

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null); // { discountAmount, message }
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);

  // Order notes
  const [orderNotes, setOrderNotes] = useState('');

  // Normalize Australian state abbreviations to full names
  const normalizeState = (state) => {
    if (!state) return '';
    
    const stateMap = {
      'NSW': 'New South Wales',
      'VIC': 'Victoria',
      'QLD': 'Queensland',
      'SA': 'South Australia',
      'WA': 'Western Australia',
      'TAS': 'Tasmania',
      'NT': 'Northern Territory',
      'ACT': 'Australian Capital Territory',
      // Also handle common variations
      'New South Wale': 'New South Wales', // Fix typo if present
    };
    
    // Check if it's an abbreviation
    const normalized = stateMap[state.toUpperCase()] || stateMap[state];
    
    // If not found in map, return original (might already be full name)
    return normalized || state;
  };

  // Google Places Autocomplete on the Street Address input.
  // Lazy-loads the Maps JS API on first checkout reach. When the customer picks an
  // address, every shipping field is patched in one setState — which trips the
  // existing fetchShippingCost effect and updates the calculator immediately.
  useEffect(() => {
    let active = true;
    let autocomplete = null;
    let listener = null;

    loadGooglePlaces().then((places) => {
      if (!active) return;
      if (!places || !streetInputRef.current) return;

      try {
        autocomplete = new places.Autocomplete(streetInputRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address'],
        });

        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place || !place.address_components) return;

          const get = (type, useShort = false) => {
            const c = place.address_components.find((x) => x.types.includes(type));
            if (!c) return '';
            return useShort ? c.short_name : c.long_name;
          };

          const streetNumber = get('street_number');
          const route = get('route');
          const street = [streetNumber, route].filter(Boolean).join(' ').trim();
          const city = get('locality') || get('postal_town') || get('sublocality') || '';
          const stateRaw = get('administrative_area_level_1');
          const postalCode = get('postal_code');
          const countryCode = countryToCodeFromGoogleOrLabel(get('country'));
          const state =
            isAustraliaCountryCode(countryCode) ? normalizeState(stateRaw) : (stateRaw || '');

          setShippingAddress((prev) => ({
            ...prev,
            street: street || prev.street,
            city: city || prev.city,
            state: state || prev.state,
            postalCode: postalCode || prev.postalCode,
            country: countryCode || prev.country,
          }));
        });

        setAutocompleteReady(true);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[CustomCheckout] Places Autocomplete init failed:', err.message);
        }
      }
    });

    return () => {
      active = false;
      if (listener && typeof listener.remove === 'function') listener.remove();
      // Clean up the floating dropdown so it doesn't linger after unmount
      document.querySelectorAll('.pac-container').forEach((el) => el.remove());
    };
    // Effect runs once on mount — Autocomplete reattaches via the ref on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    setDiscountError('');
    setDiscountApplied(null);
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/orders/validate-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode.trim().toUpperCase(), subtotal }),
      });
      const data = await res.json();
      if (data.valid) {
        setDiscountApplied(data);
      } else {
        setDiscountError(data.message || 'Invalid code');
      }
    } catch {
      setDiscountError('Could not validate code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      console.log('🔍 Auto-filling checkout with user data:', user);
      setCustomerInfo({
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        phone: user.phone || ''
      });
      
      if (user.address) {
        const countryCode = countryToCodeFromGoogleOrLabel(user.address.country);
        const rawState = user.address.state || '';
        const stateVal = isAustraliaCountryCode(countryCode) ? normalizeState(rawState) : rawState;

        setShippingAddress({
          street: user.address.street || '',
          apartment: '',
          city: user.address.city || '',
          state: stateVal,
          postalCode: user.address.postcode || '',
          country: countryCode,
        });
      }
    }
  }, [user]);

  // Rainbow Sails shop customer account (JWT) — prefill contact + saved shipping address.
  useEffect(() => {
    if (!isShopCustomer || !customer) return;
    setCustomerInfo((prev) => ({
      name:
        prev.name ||
        `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
        customer.email ||
        '',
      email: prev.email || customer.email || '',
      phone: prev.phone || customer.phone || '',
    }));

    const saved = parseCustomerShippingAddress(customer.shippingAddress);
    if (saved) {
      setShippingAddress((prev) => ({
        street: saved.street || prev.street,
        apartment: saved.apartment || prev.apartment,
        city: saved.city || prev.city,
        state: saved.state || prev.state,
        postalCode: saved.postalCode || prev.postalCode,
        country: saved.country || prev.country,
      }));
      if (isCompleteShippingAddress(saved)) {
        setEditShippingAddress(false);
      }
    }
  }, [isShopCustomer, customer]);
  
  // const [stripeError, setStripeError] = useState(null); // Unused for now
  
  const [shippingCost, setShippingCost] = useState(getCachedShippingCost() || 0);
  const [shippingCostLoading, setShippingCostLoading] = useState(true);
  const [deliveryMode, setDeliveryMode] = useState('ship');
  const [pickupEligible, setPickupEligible] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(FREE_SHIP_FALLBACK);
  const [showFreeShippingPromo, setShowFreeShippingPromo] = useState(true);
  const [subtotalFreeShippingEnabled, setSubtotalFreeShippingEnabled] = useState(true);
  const [requiresManualFreightQuote, setRequiresManualFreightQuote] = useState(false);
  /** When the API returns multiple carrier rows (e.g. AusPost stub), the customer must pick one; id is sent on create order. */
  const [shippingOptionsList, setShippingOptionsList] = useState([]);
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState('');

  const hasPhysicalProduct = (cartItems || []).some((item) => cartLineNeedsShippingQuote(item));

  useEffect(() => {
    const fetchShippingCost = async () => {
      try {
        if (!hasPhysicalProduct) {
          setShippingCost(0);
          setPickupEligible(false);
          setShowFreeShippingPromo(true);
          setRequiresManualFreightQuote(false);
          setShippingCostLoading(false);
          return;
        }

        const isProduction =
          typeof window !== 'undefined' &&
          (window.location.hostname.includes('rainbow-sails.shop') ||
            (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const apiBaseV1 =
          process.env.REACT_APP_API_URL ||
          (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
        const businessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();

        if (!businessId) {
          const subEligibleFs = getSubtotalForFreeShippingEligible(cartItems || []);
          const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: '' });
          setFreeShippingThreshold(d.freeThreshold);
          setShowFreeShippingPromo(d.showFreeShippingPromo);
          setSubtotalFreeShippingEnabled(d.subtotalFreeShippingEnabled !== false);
          if (d.showFreeShippingPromo) {
            try {
              analyticsService.trackEvent('free_shipping_threshold_view', { freeThreshold: d.freeThreshold, source: 'checkout' });
            } catch (_) { /* no-op */ }
          }
          const threshold = d.freeThreshold;
          if (d.subtotalFreeShippingEnabled !== false && subEligibleFs > threshold) setShippingCost(0);
          else {
            const cached = getCachedShippingCost();
            setShippingCost(cached != null ? cached : 10);
          }
          setRequiresManualFreightQuote(false);
          setShippingCostLoading(false);
          return;
        }

        const q = await quoteShippingFromCart({
          apiBaseV1,
          businessId,
          cartItems,
          deliveryMode,
          shippingAddress,
        });
        if (deliveryMode === 'pickup' && !q.pickupEligible) {
          setDeliveryMode('ship');
        }
        setPickupEligible(q.pickupEligible);
        setFreeShippingThreshold(q.freeThreshold);
        setShowFreeShippingPromo(q.showFreeShippingPromo !== false);
        setSubtotalFreeShippingEnabled(q.subtotalFreeShippingEnabled !== false);
        setRequiresManualFreightQuote(!!q.requiresManualQuote);
        const opts = q.shippingOptions || [];
        setShippingOptionsList(opts);
        if (opts.length > 0) {
          setSelectedShippingOptionId(q.appliedShippingOptionId || opts[0].id || '');
        } else {
          setSelectedShippingOptionId('');
        }
        setShippingCost(q.shippingCost);
        setCachedShippingCost(q.shippingCost);
        try {
          if (q.showFreeShippingPromo !== false) {
            analyticsService.trackEvent('free_shipping_threshold_view', { freeThreshold: q.freeThreshold, source: 'checkout' });
          }
          analyticsService.trackEvent('free_shipping_quote_applied', {
            freeThreshold: q.freeThreshold,
            showFreeShippingPromo: q.showFreeShippingPromo !== false,
            deliveryMode,
            shippingCost: q.shippingCost,
          });
        } catch (_) { /* no-op */ }
      } catch (error) {
        console.error('Error fetching shipping quote:', error);
        setRequiresManualFreightQuote(false);
        const cached = getCachedShippingCost();
        if (cached != null) setShippingCost(cached);
      } finally {
        setShippingCostLoading(false);
      }
    };

    fetchShippingCost();
    // getTotalPrice is read inside via closure; it's now memoized in CartContext but we
    // intentionally exclude it from the deps so a future regression in the context can't
    // trigger this effect (and the parent's PaymentIntent loop) on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartItems,
    deliveryMode,
    shippingAddress.street,
    shippingAddress.city,
    shippingAddress.state,
    shippingAddress.postalCode,
    shippingAddress.country,
  ]);
  
  // Calculate costs — prices are GST-inclusive; `tax` is the GST *portion* for display only
  const subtotal = getTotalPrice();
  const subtotalEligibleForFreeShipping = getSubtotalForFreeShippingEligible(cartItems || []);
  const discountAmount = discountApplied ? Math.min(discountApplied.discountAmount, subtotal) : 0;
  const nonDonationSubtotal = (cartItems || [])
    .filter(item => (item.type !== 'donation' && item.productType !== 'donation'))
    .reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  let tax = 0;
  if (!isDonationOnly && subtotal > 0) {
    const discountOnNonDonation = discountAmount * (nonDonationSubtotal / subtotal);
    const netNonDonation = Math.max(0, nonDonationSubtotal - discountOnNonDonation);
    tax = netNonDonation * (0.1 / 1.1);
  }
  const total = Math.max(0, subtotal - discountAmount) + (shippingCost || 0);

  // Single source of truth for Stripe: same dollars as the button (includes discount + shipping the form uses).
  // Debounced 200ms so a mount-time flicker of `shippingCost` (cache → API quote) doesn't push two
  // values up and trigger the parent to recreate the PaymentIntent twice in quick succession.
  useEffect(() => {
    if (typeof onPayableAmountCentsChange !== 'function') return undefined;
    const cents = Math.max(0, Math.round(total * 100));
    const t = setTimeout(() => {
      onPayableAmountCentsChange(cents);
    }, 200);
    return () => clearTimeout(t);
  }, [total, onPayableAmountCentsChange]);
  
  // Track checkout start
  useEffect(() => {
    analyticsService.trackCheckoutStart(cartItems, total);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleSubmit = async (e) => {
    console.log('🔵 FORM SUBMITTED - handleSubmit called');
    e.preventDefault();

    console.log('🔵 Stripe available:', !!stripe);
    console.log('🔵 Elements available:', !!elements);
    console.log('🔵 Customer info:', customerInfo);
    console.log('🔵 Shipping address:', shippingAddress);
    console.log('🔵 Payment method:', paymentMethod);

    // Only require Stripe/Elements if not in bypass mode
    if (paymentMethod === 'card' && !BYPASS_PAYMENT && (!stripe || !elements)) {
      console.log('❌ Stripe or Elements not available for card payment, returning');
      return;
    }

    // Validation
    if (!customerInfo.name || !customerInfo.email) {
      console.log('❌ Validation failed: missing customer info');
      setError('Please fill in customer information');
      return;
    }

    if (
      !isDonationOnly &&
      deliveryMode === 'ship' &&
      (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postalCode)
    ) {
      console.log('❌ Validation failed: missing shipping address fields');
      console.log('🔍 Shipping address state:', shippingAddress);
      setError('Please fill in all required shipping address fields (street, city, state, postal code)');
      return;
    }

    console.log('✅ All validation passed, starting payment processing...');
    setProcessing(true);
    setError(null);

    logger.log('info', 'checkout', 'Processing payment', {
      total: total,
      itemCount: cartItems.length,
      paymentMethod
    });

    // Full phone with the selected dialing code prefix (e.g. "+61 412 345 678").
    const fullPhone = customerInfo.phone
      ? `${phoneDialCode} ${String(customerInfo.phone).trim()}`.trim()
      : '';

    // Shared order notes (customer contact, gift/notes, and courier "if no one is home" preference).
    const includeDeliveryPref =
      deliveryPreference && (!hasPhysicalProduct || deliveryMode === 'ship') && !isDonationOnly;
    const customerNotesValue = [
      `Customer: ${customerInfo.name} (${customerInfo.email})${fullPhone ? ` - Phone: ${fullPhone}` : ''}`,
      orderNotes ? `Notes: ${orderNotes}` : null,
      includeDeliveryPref ? `Delivery preference: ${deliveryPreference}` : null,
    ].filter(Boolean).join(' | ');

    if (paymentMethod === 'afterpay') {
      try {
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
        const merchantReference = `ZEE-AFTERPAY-${Date.now()}`;
        const businessId = (process.env.REACT_APP_BUSINESS_ID || '').trim() || undefined;

        // Order body matching POST /api/v1/orders (createOrderSchema). Posted on return with afterpayToken.
        const afterpayOrderData = {
          businessId,
          items: cartItems.map((item) => {
            if (item.type === 'event' || item.eventId) {
              return {
                eventId: item.eventId || String(item.id).replace(/^event-/, ''),
                quantity: item.quantity,
              };
            }
            return {
              productId: item.productId || item.id,
              variantId: item.variantId,
              quantity: item.quantity,
              price: item.price,
            };
          }),
          shippingAddress:
            isDonationOnly || (hasPhysicalProduct && deliveryMode === 'pickup')
              ? undefined
              : {
                  line1: shippingAddress.street,
                  line2: shippingAddress.apartment || undefined,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  postalCode: shippingAddress.postalCode,
                  country: shippingAddress.country,
                },
          discountCode: discountApplied ? discountCode.trim().toUpperCase() : undefined,
          customerEmail: (customerInfo.email || '').trim() || undefined,
          customerName: (customerInfo.name || '').trim() || undefined,
          deliveryMode: hasPhysicalProduct ? deliveryMode : 'ship',
          ...(hasPhysicalProduct &&
          deliveryMode === 'ship' &&
          selectedShippingOptionId &&
          shippingOptionsList.length > 0
            ? { selectedShippingOptionId }
            : {}),
          customerNotes: customerNotesValue,
        };

        const response = await fetch(`${backendUrl}/api/v1/orders/afterpay/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            businessId,
            amount: total,
            currency: 'AUD',
            merchantReference,
            customer: {
              name: customerInfo.name,
              email: customerInfo.email,
              phone: fullPhone || undefined,
            },
            items: cartItems.map((item) => ({
              name: item.title || item.name || item.productName || 'Item',
              quantity: item.quantity,
              price: item.price,
            })),
            shippingAddress: afterpayOrderData.shippingAddress,
            redirectUrls: {
              confirmUrl: `${window.location.origin}/checkout/afterpay-return?status=approved`,
              cancelUrl: `${window.location.origin}/checkout/afterpay-return?status=cancelled`
            }
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const message = data?.message || data?.error || `Afterpay checkout failed (${response.status})`;
          throw new Error(message);
        }

        analyticsService.trackCheckoutStep('afterpay_redirect', {
          token: data.token,
          merchantReference: data.merchantReference || merchantReference
        });

        sessionStorage.setItem(
          AFTERPAY_SESSION_STORAGE_KEY,
          JSON.stringify({
            token: data.token,
            merchantReference: data.merchantReference || merchantReference,
            checkoutUrl: data.checkoutUrl,
            order: afterpayOrderData,
            createdAt: Date.now()
          })
        );

        window.location.href = data.checkoutUrl;
        return;
      } catch (afterpayError) {
        console.error('❌ Afterpay checkout error:', afterpayError);
        setError(afterpayError.message || 'Failed to start Afterpay checkout');
        setProcessing(false);
        logger.log('error', 'checkout', 'Afterpay checkout failed', {
          error: afterpayError.message
        });
        return;
      }
    }

    try {
      let paymentIntent = null;

      if (BYPASS_PAYMENT) {
        console.log('⚠️ BYPASS MODE: Skipping Stripe payment, creating test order');
        paymentIntent = {
          id: 'test_payment_' + Date.now(),
          status: 'succeeded'
        };
      } else {
        console.log('🔍 Starting Stripe payment confirmation...');
        console.log('🔍 Stripe instance:', !!stripe);
        console.log('🔍 Elements instance:', !!elements);

        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/checkout/success`,
          },
          redirect: 'if_required'
        });

        console.log('🔍 Stripe payment result:', result);

        const { error: stripeError, paymentIntent: intent } = result;

        if (stripeError) {
          console.error('❌ Stripe error:', stripeError);
          setError(stripeError.message);
          setProcessing(false);
          logger.log('error', 'checkout', 'Payment failed', { 
            error: stripeError.message,
            type: stripeError.type,
            code: stripeError.code
          });
          return;
        }

        paymentIntent = intent;

        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
          console.error('❌ Payment not successful. Status:', paymentIntent?.status);
          setError('Payment was not successful. Please try again.');
          setProcessing(false);
          logger.log('error', 'checkout', 'Payment not succeeded', { 
            status: paymentIntent?.status
          });
          return;
        }
      }

      logger.log('info', 'checkout', 'Payment successful', {
        paymentIntentId: paymentIntent.id,
        status: paymentIntent.status
      });

      analyticsService.trackCheckoutStep('payment_completed', {
        paymentIntentId: paymentIntent.id
      });

      // Format order data to match backend schema
      const orderData = {
        items: cartItems.map((item) => {
          if (item.type === 'event' || item.eventId) {
            return {
              eventId: item.eventId || String(item.id).replace(/^event-/, ''),
              quantity: item.quantity,
            };
          }
          return {
            productId: item.productId || item.id,
            variantId: item.variantId,
            quantity: item.quantity,
            price: item.price,
          };
        }),
        shippingAddress:
          isDonationOnly || (hasPhysicalProduct && deliveryMode === 'pickup')
            ? undefined
            : {
                line1: shippingAddress.street,
                line2: shippingAddress.apartment || undefined,
                city: shippingAddress.city,
                state: shippingAddress.state,
                postalCode: shippingAddress.postalCode,
                country: shippingAddress.country,
              },
        discountCode: discountApplied ? discountCode.trim().toUpperCase() : undefined,
        /** Client already paid — link order to this PI and skip duplicate charge on server */
        stripePaymentIntentId:
          paymentIntent?.id && String(paymentIntent.id).startsWith('pi_') ? paymentIntent.id : undefined,
        customerEmail: (customerInfo.email || '').trim() || undefined,
        customerName: (customerInfo.name || '').trim() || undefined,
        deliveryMode: hasPhysicalProduct ? deliveryMode : 'ship',
        ...(hasPhysicalProduct &&
        deliveryMode === 'ship' &&
        selectedShippingOptionId &&
        shippingOptionsList.length > 0
          ? { selectedShippingOptionId }
          : {}),
        customerNotes: customerNotesValue,
      };

      logger.log('info', 'checkout', 'Creating order', orderData);

      // Use the same API config as the rest of the frontend
      // Use the same API config as the rest of the frontend
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('rainbow-sails.shop') ||
         (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 
        (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
      // Use v1 endpoint - it handles orders with or without payment
      const response = await fetch(`${backendUrl}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      console.log('🔍 Backend response:', result);

      // Backend returns { order, ... } or { error, ... }
      if (!response.ok || result.error) {
        throw new Error(result.message || result.error || 'Failed to create order');
      }

      const orderNumber = result.order?.orderNumber || result.orderNumber;
      const printfulId = result.printfulOrderId || result.order?.printfulOrderId || '';
      logger.log('info', 'checkout', 'Order created', {
        orderNumber: orderNumber,
        printfulOrderId: printfulId
      });

      try {
        const o = result.order;
        if (o && orderNumber) {
          const shopBusinessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();
          const orderKind = computeOrderKind(cartItems, isDonationOnly);
          const firstName = (customerInfo.name || '').trim().split(/\s+/)[0] || '';
          const lineSummary = lineSummaryFromCart(cartItems) ||
            (o.items || []).map((it) => it.name).filter(Boolean).join(' · ') ||
            '';
          const receipt = {
            orderId: o.id,
            orderNumber,
            isDonationOnly,
            businessId: shopBusinessId,
            firstName,
            orderKind,
            lineSummary,
            printfulOrderId: printfulId || o.printfulOrderId || '',
            subtotal: o.subtotal,
            tax: o.tax,
            shipping: o.shipping,
            discount: o.discount,
            total: o.total,
            items: (o.items || []).map((it) => ({
              name: it.name,
              quantity: it.quantity,
              price: it.price,
              total: it.total,
            })),
            createdAt: o.createdAt,
            digitalDownloadGrants: Array.isArray(result.digitalDownloadGrants)
              ? result.digitalDownloadGrants
              : [],
          };
          sessionStorage.setItem(ORDER_RECEIPT_STORAGE_PREFIX + orderNumber, JSON.stringify(receipt));
        }
      } catch (receiptErr) {
        console.warn('[checkout] Could not cache receipt for success page', receiptErr);
      }

      // Auto-save delivery address to profile for signed-in customers (no checkbox needed).
      if (
        isShopCustomer &&
        !isDonationOnly &&
        hasPhysicalProduct &&
        deliveryMode === 'ship'
      ) {
        try {
          await persistCustomerShippingAfterOrder({
            getAuthHeaders,
            refreshCustomer,
            shippingAddress,
            phone: fullPhone || undefined,
          });
        } catch {
          /* non-critical — order already succeeded */
        }
      }

      setOrderCompleted(true);
      clearCart();
      {
        const bid = (process.env.REACT_APP_BUSINESS_ID || '').trim();
        const fn = (customerInfo.name || '').trim().split(/\s+/)[0] || '';
        const q = new URLSearchParams();
        q.set('order', orderNumber);
        q.set('printful', printfulId || '');
        if (bid) q.set('bid', bid);
        if (fn) q.set('fn', fn);
        navigate(`/checkout/success?${q.toString()}`);
      }

    } catch (err) {
      console.error('Order creation error:', err);
      setError(err.message || 'Failed to create order');
      setProcessing(false);

      logger.log('error', 'checkout', 'Order creation failed', {
        error: err.message
      });
    }
  };
  
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
      minHeight: '100vh',
      padding: isMobile ? '1rem 0.5rem' : '2rem 1rem',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      <form onSubmit={handleSubmit} style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: isMobile ? '1.5rem' : '3rem',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Rainbow Sails Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '4rem',
            opacity: 0.1,
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}>
            🌈
          </div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            textShadow: '0 0 30px rgba(139, 92, 246, 0.5)'
          }}>
            Rainbow Sails
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: 0
          }}>
            Your Order Information
          </p>
        </div>
      
      {error && (
        <div style={{
          padding: '1rem',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '8px',
          color: '#fecaca',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}
      
      {/* Customer Information */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem', 
          background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          fontWeight: 'bold'
        }}>
          {isShopCustomer ? '✨ Your account' : user ? '✨ Your Information' : '👤 Customer Information'}
        </h2>
        {isShopCustomer && (
          <div style={{
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.2)',
            border: '1px solid rgba(34, 197, 94, 0.5)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            color: '#bbf7d0',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}>
            Checking out as{' '}
            <strong>{customerInfo.name || customer?.email}</strong>
            {customerInfo.email ? ` · ${customerInfo.email}` : ''}
          </div>
        )}
        {user && !isShopCustomer && (
          <div style={{
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.2)',
            border: '1px solid rgba(34, 197, 94, 0.5)',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            color: '#bbf7d0',
            fontSize: '0.9rem'
          }}>
            ✅ Logged in as {user.email} - Your details have been pre-filled
          </div>
        )}
        <div style={{ display: 'grid', gap: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
          {!isShopCustomer && (
          <input
            type="text"
            placeholder="Full Name *"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            required
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '1.2rem',
              borderRadius: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1.1rem',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.8)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'}
          />
          )}
          {!isShopCustomer && (
          <input
            type="email"
            placeholder="Email Address *"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            required
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1rem',
              boxSizing: 'border-box'
            }}
          />
          )}
          {(!isShopCustomer || !customerInfo.phone) && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              name="phoneDialCode"
              className="checkout-select"
              aria-label="Phone country code"
              value={phoneDialCode}
              onChange={(e) => setPhoneDialCode(e.target.value)}
              style={{
                flex: '0 0 auto',
                width: isMobile ? '8rem' : '9.5rem',
                padding: isMobile ? '1rem 0.5rem' : '0.75rem 0.5rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              {CHECKOUT_DIAL_CODES.map(({ code, dial, label, flag }) => (
                <option key={`${code}-${dial}`} value={dial}>
                  {`${flag ? `${flag} ` : ''}${dial} ${code}`}
                </option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Phone Number"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
              style={{
                flex: 1,
                minWidth: 0,
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          )}
        </div>
      </div>
      
      {/* Delivery / address — hidden for donation-only carts */}
      {!isDonationOnly && <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'white' }}>
          Delivery
        </h2>
        {hasPhysicalProduct && pickupEligible && (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <label
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border:
                  deliveryMode === 'ship'
                    ? '2px solid rgba(139, 92, 246, 0.8)'
                    : '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <input
                type="radio"
                name="deliveryMode"
                checked={deliveryMode === 'ship'}
                onChange={() => setDeliveryMode('ship')}
                style={{ accentColor: '#8b5cf6' }}
              />
              Ship to my address
            </label>
            <label
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border:
                  deliveryMode === 'pickup'
                    ? '2px solid rgba(139, 92, 246, 0.8)'
                    : '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <input
                type="radio"
                name="deliveryMode"
                checked={deliveryMode === 'pickup'}
                onChange={() => setDeliveryMode('pickup')}
                style={{ accentColor: '#8b5cf6' }}
              />
              Pick up at the store (self-fulfilled items only)
            </label>
          </div>
        )}
        {hasPhysicalProduct && deliveryMode === 'pickup' && (
          <p style={{ color: '#a7f3d0', fontSize: '0.95rem', marginBottom: '1rem' }}>
            No delivery charge. We will prepare your order for collection at the business location. You will receive confirmation with pickup details.
          </p>
        )}
        {(!hasPhysicalProduct || deliveryMode === 'ship') && (
        <>
        {usingSavedProfileShipping ? (
          <div style={{
            padding: '1rem 1.25rem',
            borderRadius: '12px',
            background: 'rgba(16, 185, 129, 0.12)',
            border: '1px solid rgba(16, 185, 129, 0.35)',
            marginBottom: '1rem',
          }}>
            <div style={{ color: '#a7f3d0', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              Deliver to
            </div>
            <div style={{ color: 'white', fontSize: '1rem', lineHeight: 1.5, marginBottom: '10px' }}>
              {formatShippingAddressSummary(shippingAddress)}
            </div>
            <button
              type="button"
              onClick={() => setEditShippingAddress(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#c4b5fd',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Use a different address
            </button>
          </div>
        ) : (
        <div style={{ display: 'grid', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
          <div>
            <input
              ref={streetInputRef}
              type="text"
              placeholder="Street Address *"
              value={shippingAddress.street}
              onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
              required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
              autoComplete="street-address"
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            {autocompleteReady && (
              <div style={{
                marginTop: '4px',
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.55)',
              }}>
                Start typing to auto-fill from Google
              </div>
            )}
          </div>
          <input
            type="text"
            placeholder="Apartment, Suite, etc."
            value={shippingAddress.apartment}
            onChange={(e) => setShippingAddress({ ...shippingAddress, apartment: e.target.value })}
            autoComplete="address-line2"
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', 
            gap: '1rem',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <input
              type="text"
              placeholder="City *"
              value={shippingAddress.city}
              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
              required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
              autoComplete="address-level2"
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            {isAustraliaCountryCode(shippingAddress.country) ? (
              <select
                name="state"
                className="checkout-select"
                value={shippingAddress.state}
                onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
                autoComplete="address-level1"
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">State / territory...</option>
                <option value="Australian Capital Territory">Australian Capital Territory</option>
                <option value="New South Wales">New South Wales</option>
                <option value="Northern Territory">Northern Territory</option>
                <option value="Queensland">Queensland</option>
                <option value="South Australia">South Australia</option>
                <option value="Tasmania">Tasmania</option>
                <option value="Victoria">Victoria</option>
                <option value="Western Australia">Western Australia</option>
              </select>
            ) : (
              <input
                type="text"
                name="state"
                placeholder="State / Province / Region *"
                value={shippingAddress.state}
                onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
                autoComplete="address-level1"
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            )}
            <input
              type="text"
              placeholder="Postal Code *"
              value={shippingAddress.postalCode}
              onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
              required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
              autoComplete="postal-code"
              style={{
                width: '100%',
                padding: isMobile ? '1rem' : '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <label style={{ display: 'block', marginBottom: '0.35rem', color: '#d1d5db', fontSize: '0.85rem' }}>
            Country *
          </label>
          <select
            name="country"
            className="checkout-select"
            value={shippingAddress.country}
            onChange={(e) => {
              const next = e.target.value;
              setShippingAddress((prev) => ({
                ...prev,
                country: next,
                state: isAustraliaCountryCode(next) ? prev.state : '',
              }));
            }}
            required={!isDonationOnly && (!hasPhysicalProduct || deliveryMode === 'ship')}
            autoComplete="country"
            style={{
              width: '100%',
              padding: isMobile ? '1rem' : '0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {!CHECKOUT_COUNTRIES.some((c) => c.code === shippingAddress.country) &&
              shippingAddress.country && (
                <option value={shippingAddress.country}>{shippingAddress.country} (from address)</option>
              )}
            {CHECKOUT_COUNTRIES.map(({ code, label, flag }) => (
              <option key={code} value={code}>
                {flag ? `${flag} ${label}` : label}
              </option>
            ))}
          </select>
        </div>
        )}
        <label style={{ display: 'block', marginTop: '1rem', marginBottom: '0.35rem', color: '#d1d5db', fontSize: '0.85rem' }}>
          If no one is home
        </label>
        <select
          name="deliveryPreference"
          className="checkout-select"
          value={deliveryPreference}
          onChange={(e) => setDeliveryPreference(e.target.value)}
          style={{
            width: '100%',
            padding: isMobile ? '1rem' : '0.75rem',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            fontSize: '1rem',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <option value="">No preference</option>
          <option value="Leave in a safe place">Leave in a safe place</option>
          <option value="Take to nearest post office / depot">Take to nearest post office / depot</option>
        </select>
        {hasSavedProfileShipping && editShippingAddress && (
          <button
            type="button"
            onClick={() => {
              setEditShippingAddress(false);
              if (savedProfileShipping) {
                setShippingAddress({
                  street: savedProfileShipping.street,
                  apartment: savedProfileShipping.apartment || '',
                  city: savedProfileShipping.city,
                  state: savedProfileShipping.state,
                  postalCode: savedProfileShipping.postalCode,
                  country: savedProfileShipping.country,
                });
              }
            }}
            style={{
              marginTop: '8px',
              background: 'transparent',
              border: 'none',
              color: '#c4b5fd',
              fontSize: '0.9rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Use saved address instead
          </button>
        )}
        </>
        )}
      </div>}

      {/* Order notes */}
      <div style={{ marginBottom: '2rem' }}>
        <textarea
          placeholder="Order notes (optional) — gift message, delivery instructions, etc."
          value={orderNotes}
          onChange={e => setOrderNotes(e.target.value)}
          rows={2}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)',
            color: 'white', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Order Summary */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'white' }}>
          Order Summary
        </h2>
        {BYPASS_PAYMENT && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              background: 'rgba(234, 179, 8, 0.2)',
              border: '1px solid rgba(250, 204, 21, 0.5)',
              color: '#fef9c3',
              fontSize: '0.9rem',
            }}
          >
            Test mode: payment bypass is on (<code style={{ color: '#fff' }}>REACT_APP_CHECKOUT_BYPASS=true</code>).
            Turn it off for live charges.
          </div>
        )}
        
        {cartItems.map((item) => (
          <div key={item.id} style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <span style={{ color: '#d1d5db' }}>
              {item.name} — {item.quantity} × ${Number(item.price || 0).toFixed(2)}
            </span>
            <span style={{ color: 'white', fontWeight: '600' }}>
              ${(Number(item.price || 0) * item.quantity).toFixed(2)}
            </span>
          </div>
        ))}
        
        {/* Discount code */}
        <div style={{ margin: '1rem 0', display: 'flex', gap: '8px' }}>
          <input
            type="text"
            placeholder="Discount / coupon code"
            value={discountCode}
            onChange={e => { setDiscountCode(e.target.value); setDiscountError(''); if (!e.target.value.trim()) setDiscountApplied(null); }}
            onKeyDown={e => e.key === 'Enter' && handleApplyDiscount()}
            style={{
              flex: 1, padding: '0.6rem 0.9rem', borderRadius: '8px',
              border: discountApplied ? '1px solid #10b981' : discountError ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '0.95rem',
            }}
          />
          <button
            type="button"
            onClick={handleApplyDiscount}
            disabled={discountLoading || !discountCode.trim()}
            style={{
              padding: '0.6rem 1.1rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: discountApplied ? '#10b981' : 'rgba(139,92,246,0.7)', color: 'white',
              fontWeight: '600', fontSize: '0.9rem', opacity: discountLoading ? 0.6 : 1,
            }}
          >
            {discountLoading ? '…' : discountApplied ? '✓' : 'Apply'}
          </button>
        </div>
        {discountApplied && (
          <p style={{ color: '#10b981', fontSize: '13px', margin: '-4px 0 10px 0' }}>🎉 {discountApplied.message}</p>
        )}
        {discountError && (
          <p style={{ color: '#f87171', fontSize: '13px', margin: '-4px 0 10px 0' }}>✗ {discountError}</p>
        )}

        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid rgba(255, 255, 255, 0.2)' }}>
          {requiresManualFreightQuote && hasPhysicalProduct && deliveryMode === 'ship' && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.65rem 0.85rem',
                borderRadius: '8px',
                background: 'rgba(251, 191, 36, 0.15)',
                border: '1px solid rgba(251, 191, 36, 0.45)',
                color: '#fcd34d',
                fontSize: '0.85rem',
                lineHeight: 1.45,
              }}
            >
              One or more items need a <strong>manual freight quote</strong>. Please contact the store before paying; checkout cannot complete until shipping is arranged.
            </div>
          )}
          {!isDonationOnly && (
            <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>All prices include GST (10%)</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#d1d5db' }}>Subtotal:</span>
            <span style={{ color: 'white' }}>${subtotal.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#10b981' }}>Discount ({discountCode.toUpperCase()}):</span>
              <span style={{ color: '#10b981' }}>−${discountAmount.toFixed(2)}</span>
            </div>
          )}
          {shippingOptionsList.length > 1 &&
            hasPhysicalProduct &&
            deliveryMode === 'ship' &&
            !requiresManualFreightQuote && (
              <div
                style={{
                  marginBottom: '0.85rem',
                  padding: '0.65rem 0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(0,0,0,0.2)',
                }}
                role="group"
                aria-label="Shipping method"
              >
                <div style={{ color: '#e5e7eb', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Shipping method
                </div>
                {shippingOptionsList.map((opt) => (
                  <label
                    key={opt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '0.45rem',
                      cursor: 'pointer',
                      color: '#d1d5db',
                      fontSize: '0.85rem',
                      lineHeight: 1.35,
                    }}
                  >
                    <input
                      type="radio"
                      name="shippingOption"
                      value={opt.id}
                      checked={selectedShippingOptionId === opt.id}
                      onChange={() => {
                        setSelectedShippingOptionId(opt.id);
                        setShippingCost(Number(opt.amount) || 0);
                        setCachedShippingCost(Number(opt.amount) || 0);
                      }}
                      style={{ marginTop: '0.15rem' }}
                    />
                    <span>
                      <span style={{ color: '#fff' }}>{opt.label || `${opt.carrier} — ${opt.service}`}</span>
                      <span style={{ color: '#9ca3af', marginLeft: '6px' }}>
                        ${Number(opt.amount || 0).toFixed(2)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#d1d5db' }}>
              Shipping: {showFreeShippingPromo && subtotalFreeShippingEnabled && shippingCost === 0 && subtotalEligibleForFreeShipping > freeShippingThreshold && (
                <span style={{ color: '#10b981' }}>(Free over ${Number(freeShippingThreshold).toFixed(2)} qualifying subtotal)</span>
              )}
              {shippingCost === 0 && deliveryMode === 'pickup' && hasPhysicalProduct && (
                <span style={{ color: '#10b981', marginLeft: '4px' }}>(Pickup)</span>
              )}
              {shippingCostLoading && shippingCost !== 0 && <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}> (calculating...)</span>}
            </span>
            <span style={{ color: 'white' }}>
              {shippingCostLoading && shippingCost !== 0 ? '...' : shippingCost === 0 ? <span style={{ color: '#10b981' }}>FREE</span> : `$${shippingCost.toFixed(2)}`}
            </span>
          </div>
          {!isDonationOnly && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#d1d5db' }}>GST included (est.):</span>
              <span style={{ color: 'white' }}>${tax.toFixed(2)}</span>
            </div>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '2px solid rgba(255, 255, 255, 0.3)',
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            <span style={{ color: 'white' }}>Total:</span>
            <span style={{
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}>
              ${total.toFixed(2)} AUD
            </span>
          </div>
        </div>
      </div>
      
      {/* Payment Information */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem',
          background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          fontWeight: 'bold'
        }}>
          💳 Payment Information
        </h2>
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              borderRadius: '12px',
              border: paymentMethod === 'card' ? '2px solid rgba(139, 92, 246, 0.8)' : '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.12)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={() => setPaymentMethod('card')}
              style={{ width: '20px', height: '20px', accentColor: '#8b5cf6' }}
            />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                Stripe Card • Zee now, pay now
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Pay in full instantly through Zee&apos;s secure Stripe gateway.
              </div>
            </div>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              borderRadius: '12px',
              border: paymentMethod === 'afterpay' ? '2px solid rgba(59, 130, 246, 0.8)' : '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(37, 99, 235, 0.25)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="afterpay"
              checked={paymentMethod === 'afterpay'}
              onChange={() => setPaymentMethod('afterpay')}
              style={{ width: '20px', height: '20px', accentColor: '#3b82f6' }}
            />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                Afterpay • Zee Flex Pay in 4
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                Split your Zee order into four fortnightly payments with no extra cost when you pay on time.
              </div>
            </div>
          </label>
        </div>

        {paymentMethod === 'card' ? (
          <>
            <div style={{
              padding: '2rem',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              minHeight: '200px'
            }}>
              {BYPASS_PAYMENT ? (
                <p style={{ margin: 0, color: '#111827', fontSize: '1rem', lineHeight: 1.5 }}>
                  Test bypass is on: card fields are hidden. Use <strong>Place Order</strong> below to finish the flow without Stripe (local/staging only).
                </p>
              ) : (
                <PaymentElement
                  options={{
                    // Stripe Link is a separate "save card" identity product — redundant when the
                    // customer is already signed in to Rainbow Sails (we already have their details above).
                    wallets: { link: isShopCustomer ? 'never' : 'auto' },
                  }}
                />
              )}
            </div>
            <p style={{
              marginTop: '1rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              🔒 256-bit SSL · Secure payment powered by Stripe
            </p>
          </>
        ) : (
          <div style={{
            padding: '1.5rem',
            background: 'rgba(59, 130, 246, 0.18)',
            borderRadius: '16px',
            border: '2px solid rgba(59, 130, 246, 0.35)',
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.25)'
          }}>
            <h3 style={{ marginBottom: '0.75rem', fontSize: '1.25rem', fontWeight: '700' }}>
              Zee&apos;s Afterpay Flex
            </h3>
            <p style={{ marginBottom: '0.75rem', fontSize: '1rem', color: 'rgba(255, 255, 255, 0.85)' }}>
              We&apos;ll whisk you to Afterpay to confirm the plan, then bring you back to finish your rainbow Zee receipt.
            </p>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: 0 }}>
              Eligibility is decided by Afterpay during their sign-in. No interest or fees when paid on schedule.
            </p>
          </div>
        )}
      </div>
      
      {/* Place Order Button */}
      <button
        type="submit"
        disabled={
          processing ||
          requiresManualFreightQuote ||
          (paymentMethod === 'card' && !BYPASS_PAYMENT && !stripe)
        }
        style={{
          width: '100%',
          padding: '1rem 2rem',
          fontSize: '1.25rem',
          fontWeight: 'bold',
          background: processing ? 'rgba(156, 163, 175, 0.5)' : 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          cursor: processing ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s',
          opacity: processing ? 0.6 : 1
        }}
      >
        {processing
          ? 'Processing...'
          : paymentMethod === 'afterpay'
            ? 'Continue to Afterpay with Zee'
            : `Place Order - $${total.toFixed(2)} AUD`}
      </button>
      
      <p style={{
        marginTop: '1rem',
        textAlign: 'center',
        fontSize: '0.875rem',
        color: '#9ca3af'
      }}>
        {paymentMethod === 'afterpay'
          ? '✨ You’ll complete payment on Afterpay and Zee will hold your cart until you return.'
          : '🔒 256-bit SSL · Secure payment powered by Stripe'}
      </p>
    </form>
    </div>
  );
};

const CustomCheckout = () => {
  const { cartItems, cartLoaded, getTotalPrice } = useCart();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [orderCompleted, setOrderCompleted] = useState(false);
  /** Cart total in cents the current PaymentIntent was created for — recreate PI when this changes (e.g. user removes event from cart) */
  const lastPaymentIntentAmountCentsRef = useRef(null);
  /** CheckoutForm’s payable total (cents) = same as “Place order” line — when set, must be used for PI (includes discount; parent calc did not) */
  const [formPayableCents, setFormPayableCents] = useState(null);
  const handleFormPayableCents = useCallback((c) => {
    setFormPayableCents(c);
  }, []);
  /** In-flight create-payment-intent: only the latest request may call setClientSecret (older responses would restore a higher $ amount) */
  const paymentIntentRequestGenRef = useRef(0);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeKeyLoaded, setStripeKeyLoaded] = useState(false);

  // Fetch Stripe publishable key from Business Integrations API on mount
  useEffect(() => {
    fetchStripePublishableKey().then((key) => {
      if (key) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 Stripe key loaded from integrations:', `${key.slice(0, 12)}…`);
        }
        setStripePromise(loadStripe(key));
      } else {
        console.error('❌ No Stripe publishable key found. Configure it in Business Integrations.');
        setStripePromise(Promise.resolve(null));
      }
      setStripeKeyLoaded(true);
    });
  }, []);
  
  useEffect(() => {
    logger.logComponentMount('CustomCheckout', {
      itemCount: cartItems?.length || 0,
      totalPrice: getTotalPrice ? getTotalPrice() : 0,
      cartLoaded: cartLoaded
    });
    
    // Only check if cart is empty after cart has loaded from localStorage
    // Don't redirect if we just completed an order
    if (cartLoaded && cartItems && cartItems.length === 0 && !orderCompleted) {
      navigate('/');
      return;
    }
    
    const createPaymentIntent = async () => {
      if (!stripeKeyLoaded) return; // wait for key to load
      if (!stripePromise) {
        setLoading(false);
        return;
      }
      let myRequestGen = 0;
      try {
        const subtotal = getTotalPrice();
        const subEligibleFs = getSubtotalForFreeShippingEligible(cartItems || []);
        // Match CheckoutForm: GST is in the subtotal; only add shipping. Physical carts use the shop quote (or flat + free-shipping threshold from the API when no business id).
        const hasPhysical = (cartItems || []).some((item) => cartLineNeedsShippingQuote(item));
        let calculatedShippingCost = 0;
        if (hasPhysical) {
          const isProduction = typeof window !== 'undefined' &&
            (window.location.hostname.includes('rainbow-sails.shop') ||
              (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
          const apiBaseV1 =
            process.env.REACT_APP_API_URL ||
            (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
          const businessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();
          try {
            if (businessId) {
              const q = await quoteShippingFromCart({
                apiBaseV1,
                businessId,
                cartItems,
                deliveryMode: 'ship',
                shippingAddress: { country: 'AU', state: '', postalCode: '', street: '' },
              });
              calculatedShippingCost = q.shippingCost;
              if (calculatedShippingCost > 0) {
                setCachedShippingCost(calculatedShippingCost);
              }
            } else {
              const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: '' });
              if (d.subtotalFreeShippingEnabled !== false && subEligibleFs > d.freeThreshold) {
                calculatedShippingCost = 0;
              } else {
                const cachedShipping = getCachedShippingCost();
                if (cachedShipping !== null && cachedShipping !== undefined) {
                  calculatedShippingCost = cachedShipping;
                } else {
                  const response = await fetch(`${apiBaseV1}/orders/get-shipping-cost`);
                  if (response.ok) {
                    const data = await response.json();
                    calculatedShippingCost = data.shippingCost || 0;
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error fetching shipping cost:', err);
            calculatedShippingCost = 0;
          }
        }
        const parentFallbackTotal = subtotal + calculatedShippingCost;
        const parentFallbackCents = Math.max(0, Math.round(parentFallbackTotal * 100));
        // When CheckoutForm is mounted, it owns the true charge (discount + its shipping) — no discount in parent, so this prevented matching the button
        const amountInCents = formPayableCents != null ? formPayableCents : parentFallbackCents;

        // Tolerance of 1 cent so floating-point jitter doesn't recreate the intent
        if (
          lastPaymentIntentAmountCentsRef.current != null &&
          Math.abs(lastPaymentIntentAmountCentsRef.current - amountInCents) < 1
        ) {
          setLoading(false);
          return;
        }

        myRequestGen = ++paymentIntentRequestGenRef.current;
        
        logger.log('info', 'checkout', 'Creating payment intent', {
          amount: amountInCents / 100,
          amountInCents: amountInCents,
          fromForm: formPayableCents != null
        });
        
        // Use the same API config as the rest of the frontend
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop' : 'http://localhost:4000');
        const shopBusinessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();

        const response = await fetch(`${backendUrl}/api/v1/orders/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount: amountInCents,
            currency: 'aud',
            ...(shopBusinessId ? { businessId: shopBusinessId } : {}),
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Backend error response body:', errorText);
          throw new Error(`Backend error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();

        if (!data.success) {
          console.error('❌ Backend returned success: false, message:', data.message);
          throw new Error(data.message || 'Failed to create payment intent');
        }

        if (myRequestGen !== paymentIntentRequestGenRef.current) {
          return;
        }

        lastPaymentIntentAmountCentsRef.current = amountInCents;
        setClientSecret(data.clientSecret);
        setLoading(false);

        logger.log('info', 'checkout', 'Payment intent created', {
          clientSecret: data.clientSecret ? 'received' : 'missing'
        });
        
      } catch (error) {
        if (myRequestGen > 0 && myRequestGen !== paymentIntentRequestGenRef.current) {
          return;
        }
        console.error('❌ Failed to create payment intent:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        setLoading(false);
        
        logger.log('error', 'checkout', 'Payment intent failed', {
          error: error.message,
          stack: error.stack
        });
      }
    };
    
    if (cartLoaded && cartItems && cartItems.length > 0) {
      createPaymentIntent();
    }
    // `getTotalPrice` and `navigate` are intentionally excluded — both are stable now
    // (CartContext memoization + react-router useNavigate) and listing them here was
    // causing the parent PaymentIntent effect to re-fire on every render and create a
    // new Stripe PaymentIntent ~600ms in production. See plan: checkout-pi-loop-fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, cartLoaded, formPayableCents, orderCompleted, stripeKeyLoaded, stripePromise]);

  if (stripeKeyLoaded && !stripePromise) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '520px',
          padding: '2rem',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>Stripe not configured</h2>
          <p style={{ color: '#e5e7eb', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Add your Stripe publishable key in the Dashboard under <strong>Business Integrations → Stripe</strong>.
            The shop will pick it up automatically — no rebuild needed.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#111827',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            animation: 'spin 2s linear infinite'
          }}>
            🔄
          </div>
          <div style={{ fontSize: '1.5rem', color: 'white' }}>
            Preparing checkout...
          </div>
        </div>
      </div>
    );
  }
  
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#8b5cf6',
        colorBackground: '#ffffff',
        colorText: '#111827',
        colorDanger: '#ef4444',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
        spacingUnit: '4px',
        borderRadius: '8px'
      }
    }
  };
  
  // Only render Elements when clientSecret is available
  if (!clientSecret) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '2rem',
          background: 'rgba(239, 68, 68, 0.2)',
          borderRadius: '16px',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>
            ⚠️ Checkout Error
          </h2>
          <p style={{ color: '#fecaca', marginBottom: '1.5rem' }}>
            Failed to initialize payment. Please try again.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#111827',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
      padding: '3rem 1rem'
    }}>
      <Elements key={clientSecret} stripe={stripePromise} options={options}>
        <CheckoutForm 
          clientSecret={clientSecret}
          processing={processing}
          setProcessing={setProcessing}
          error={error}
          setError={setError}
          orderCompleted={orderCompleted}
          setOrderCompleted={setOrderCompleted}
          onPayableAmountCentsChange={handleFormPayableCents}
        />
      </Elements>
    </div>
  );
};

export default CustomCheckout;

